import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useIsMobile } from '../ui/use-mobile';
import { Calendar, Clock, MapPin, BookOpen, Users } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { api } from '../../utils/api';
import { supabase } from '../../utils/supabase/client';
import { getCurrentAcademicPeriod } from '../../utils/academicYear';

interface FacultyScheduleCalendarProps {
  facultyId: string;
  academicYear?: string;
  semester?: string; // '1' | '2' | '3' | 'all'
}

// Color palette for subject blocks — each subject gets a unique color
const SUBJECT_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700 border-blue-200', accent: 'bg-blue-500', hover: 'hover:bg-blue-100', dot: 'bg-blue-500' },
  { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', accent: 'bg-emerald-500', hover: 'hover:bg-emerald-100', dot: 'bg-emerald-500' },
  { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-800', badge: 'bg-violet-100 text-violet-700 border-violet-200', accent: 'bg-violet-500', hover: 'hover:bg-violet-100', dot: 'bg-violet-500' },
  { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700 border-amber-200', accent: 'bg-amber-500', hover: 'hover:bg-amber-100', dot: 'bg-amber-500' },
  { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-800', badge: 'bg-rose-100 text-rose-700 border-rose-200', accent: 'bg-rose-500', hover: 'hover:bg-rose-100', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-50', border: 'border-cyan-300', text: 'text-cyan-800', badge: 'bg-cyan-100 text-cyan-700 border-cyan-200', accent: 'bg-cyan-500', hover: 'hover:bg-cyan-100', dot: 'bg-cyan-500' },
  { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-800', badge: 'bg-pink-100 text-pink-700 border-pink-200', accent: 'bg-pink-500', hover: 'hover:bg-pink-100', dot: 'bg-pink-500' },
  { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-800', badge: 'bg-teal-100 text-teal-700 border-teal-200', accent: 'bg-teal-500', hover: 'hover:bg-teal-100', dot: 'bg-teal-500' },
];

export function FacultyScheduleCalendar({ facultyId, academicYear, semester }: FacultyScheduleCalendarProps) {
  const isMobile = useIsMobile();
  const [allSchedules, setAllSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [hoveredSchedule, setHoveredSchedule] = useState<string | null>(null);
  const [expandedMobileCard, setExpandedMobileCard] = useState<string | null>(null);
  const [selectedClassSchedule, setSelectedClassSchedule] = useState<any | null>(null);
  const [isStudentsDialogOpen, setIsStudentsDialogOpen] = useState(false);
  const [studentsBySchedule, setStudentsBySchedule] = useState<Record<string, any[]>>({});
  const [loadingStudentsFor, setLoadingStudentsFor] = useState<string | null>(null);
  const [studentsLoadError, setStudentsLoadError] = useState<string | null>(null);

  // Default to current academic period if no filter props provided
  const { academicYear: currentAY, semester: currentSem } = getCurrentAcademicPeriod();
  const activeAcademicYear = academicYear || currentAY;
  const activeSemester = semester || String(currentSem);

  // Filter schedules by academic year and semester
  const facultySchedules = useMemo(() => {
    return allSchedules.filter(schedule => {
      const matchesYear = schedule.schoolYear === activeAcademicYear;
      const matchesSemester = activeSemester === 'all' || String(schedule.semester) === activeSemester;
      return matchesYear && matchesSemester;
    });
  }, [allSchedules, activeAcademicYear, activeSemester]);

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTo12Hour = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Format time range
  const formatTimeRange = (startTime: string, endTime: string) => {
    return `${formatTo12Hour(startTime)} - ${formatTo12Hour(endTime)}`;
  };

  const toMinutes = (time: string) => {
    const [hours, minutes = 0] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const getDurationMinutes = (schedule: any) => {
    return Math.max(0, toMinutes(schedule.endTime) - toMinutes(schedule.startTime));
  };

  const getDurationLabel = (durationMinutes: number) => {
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    }

    if (hours > 0) {
      return `${hours}h`;
    }

    return `${minutes}m`;
  };

  const loadClassStudents = async (schedule: any) => {
    if (!schedule?.id) return;

    const scheduleId = String(schedule.id);

    setSelectedClassSchedule(schedule);
    setIsStudentsDialogOpen(true);
    setStudentsLoadError(null);

    if (studentsBySchedule[scheduleId]) {
      return;
    }

    try {
      setLoadingStudentsFor(scheduleId);
      const token = await supabase.auth.getSession().then(({ data: { session } }) => session?.access_token || '');
      const response = await api.getFacultyClassStudents(facultyId, scheduleId, token);
      setStudentsBySchedule((prev) => ({
        ...prev,
        [scheduleId]: response.students || [],
      }));
    } catch (error: any) {
      console.error('Error loading class students:', error);
      setStudentsLoadError(error?.message || 'Failed to load class students.');
      setStudentsBySchedule((prev) => ({
        ...prev,
        [scheduleId]: [],
      }));
    } finally {
      setLoadingStudentsFor(null);
    }
  };

  // Get current day of week (0=Sunday, 6=Saturday)
  const today = new Date().getDay();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayAbbrev = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const timeSlots = [
    '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const token = await supabase.auth.getSession().then(({ data: { session } }) => session?.access_token || '');
        
        try {
          const response = await api.getFacultySchedules(facultyId, token);
          setAllSchedules(response.schedules || []);
        } catch (error: any) {
          console.error('Error fetching faculty schedules:', error);
          try {
            await Promise.all([
              api.getSubjects(token).catch(() => ({ subjects: [] })),
              api.getRooms(token).catch(() => ({ rooms: [] }))
            ]);
            setAllSchedules([]);
          } catch (enrichError) {
            console.error('Error enriching schedule data:', enrichError);
            setAllSchedules([]);
          }
        }
      } catch (error) {
        console.error('Error in fetch schedules:', error);
        setAllSchedules([]);
      } finally {
        setLoading(false);
      }
    };

    if (facultyId) {
      fetchSchedules();
    }
  }, [facultyId]);

  // Build a stable color map for subjects
  const subjectColorMap = useMemo(() => {
    const map = new Map<string, typeof SUBJECT_COLORS[0]>();
    const uniqueSubjects = [...new Set(facultySchedules.map(s => s.subject?.code).filter(Boolean))];
    uniqueSubjects.forEach((code, index) => {
      map.set(code, SUBJECT_COLORS[index % SUBJECT_COLORS.length]);
    });
    return map;
  }, [facultySchedules]);

  // Compute summary stats
  const summaryStats = useMemo(() => {
    const totalClasses = facultySchedules.length;
    const uniqueSubjects = new Set(facultySchedules.map(s => s.subject?.code).filter(Boolean)).size;
    const uniqueRooms = new Set(facultySchedules.map(s => s.room?.name).filter(Boolean)).size;
    let totalMinutes = 0;
    facultySchedules.forEach(s => {
      const [sh, sm = 0] = s.startTime.split(':').map(Number);
      const [eh, em = 0] = s.endTime.split(':').map(Number);
      totalMinutes += (eh * 60 + em) - (sh * 60 + sm);
    });
    const totalHours = Math.round(totalMinutes / 60 * 10) / 10;
    return { totalClasses, uniqueSubjects, uniqueRooms, totalHours };
  }, [facultySchedules]);

  const getColorForSchedule = (schedule: any) => {
    return subjectColorMap.get(schedule.subject?.code) || SUBJECT_COLORS[0];
  };

  const now = new Date();
  const nowDay = now.getDay();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const dayClassCounts = useMemo(() => {
    return days.map((_, index) => facultySchedules.filter(s => s.dayOfWeek === index).length);
  }, [facultySchedules]);

  const ongoingClass = useMemo(() => {
    return facultySchedules.find(schedule => {
      if (schedule.dayOfWeek !== nowDay) return false;
      const start = toMinutes(schedule.startTime);
      const end = toMinutes(schedule.endTime);
      return nowMinutes >= start && nowMinutes < end;
    }) || null;
  }, [facultySchedules, nowDay, nowMinutes]);

  const nextClass = useMemo(() => {
    let best: { schedule: any; dayIndex: number; dayOffset: number; startMinutes: number } | null = null;

    facultySchedules.forEach(schedule => {
      if (typeof schedule.dayOfWeek !== 'number' || !schedule.startTime) return;

      const startMinutes = toMinutes(schedule.startTime);
      let dayOffset = (schedule.dayOfWeek - nowDay + 7) % 7;

      if (dayOffset === 0 && startMinutes <= nowMinutes) {
        dayOffset = 7;
      }

      const distance = dayOffset * 1440 + startMinutes;

      if (!best) {
        best = { schedule, dayIndex: schedule.dayOfWeek, dayOffset, startMinutes };
        return;
      }

      const bestDistance = best.dayOffset * 1440 + best.startMinutes;
      if (distance < bestDistance) {
        best = { schedule, dayIndex: schedule.dayOfWeek, dayOffset, startMinutes };
      }
    });

    return best;
  }, [facultySchedules, nowDay, nowMinutes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center space-y-3">
          <div className="relative mx-auto w-12 h-12">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-emerald-500 animate-spin"></div>
          </div>
          <p className="text-sm text-muted-foreground font-medium">Loading your schedule...</p>
        </div>
      </div>
    );
  }

  const getDayName = (dayOfWeek: number) => {
    return days[dayOfWeek] || 'Unknown';
  };

  const getScheduleForTimeSlot = (dayIndex: number, timeSlot: string) => {
    return facultySchedules.find(schedule => {
      const scheduleDay = schedule.dayOfWeek;
      const [startHour, startMin = 0] = schedule.startTime.split(':').map(Number);
      const [endHour, endMin = 0] = schedule.endTime.split(':').map(Number);
      const slotHour = parseInt(timeSlot.split(':')[0]);
      
      const startTotal = startHour * 60 + startMin;
      const endTotal = endHour * 60 + endMin;
      const slotStart = slotHour * 60;
      const slotEnd = slotStart + 60;
      
      return scheduleDay === dayIndex && startTotal < slotEnd && endTotal > slotStart;
    });
  };

  const getScheduleDuration = (schedule: any) => {
    const [startHour] = schedule.startTime.split(':').map(Number);
    const [endHour, endMin = 0] = schedule.endTime.split(':').map(Number);
    const lastSlot = endMin > 0 ? endHour : endHour - 1;
    return Math.max(1, lastSlot - startHour + 1);
  };

  const selectedScheduleId = selectedClassSchedule ? String(selectedClassSchedule.id) : '';
  const selectedClassStudents = selectedScheduleId ? (studentsBySchedule[selectedScheduleId] || []) : [];
  const isLoadingSelectedClassStudents = selectedScheduleId && loadingStudentsFor === selectedScheduleId;

  const StudentsListDialog = () => (
    <Dialog
      open={isStudentsDialogOpen}
      onOpenChange={(open) => {
        setIsStudentsDialogOpen(open);
        if (!open) {
          setStudentsLoadError(null);
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            Students List
          </DialogTitle>
          <DialogDescription>
            {selectedClassSchedule
              ? `${selectedClassSchedule.subject?.code || 'Class'} - ${selectedClassSchedule.subject?.name || ''} (${formatTimeRange(selectedClassSchedule.startTime, selectedClassSchedule.endTime)})`
              : 'Students enrolled in this class'}
          </DialogDescription>
        </DialogHeader>

        {isLoadingSelectedClassStudents ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading students list...</div>
        ) : studentsLoadError ? (
          <div className="py-8 text-center">
            <p className="text-sm text-red-600">{studentsLoadError}</p>
          </div>
        ) : selectedClassStudents.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No students enrolled in this class yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedClassStudents.map((student: any, index: number) => (
              <div key={student.id || `${student.studentNumber}-${index}`} className="rounded-lg border border-slate-200 p-3 sm:p-3.5 bg-white hover:bg-slate-50/60 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{student.name || 'Unnamed Student'}</p>
                    <p className="text-xs text-muted-foreground truncate">{student.email || 'No email'}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] sm:text-xs">{student.studentNumber || student.idNumber || 'N/A'}</Badge>
                    {student.yearLevel && (
                      <Badge variant="secondary" className="text-[10px] sm:text-xs">Year {student.yearLevel}{student.section ? ` - ${student.section}` : ''}</Badge>
                    )}
                    {student.course && (
                      <Badge variant="secondary" className="text-[10px] sm:text-xs">{student.course}</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // ----- Reusable sub-components -----

  const SummaryBar = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
      {[
        { label: 'Classes/Week', value: summaryStats.totalClasses, icon: BookOpen, color: 'text-blue-600', bg: 'bg-gradient-to-br from-blue-50 to-blue-100/60', iconBg: 'bg-blue-100' },
        { label: 'Subjects', value: summaryStats.uniqueSubjects, icon: Calendar, color: 'text-emerald-600', bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/60', iconBg: 'bg-emerald-100' },
        { label: 'Rooms Used', value: summaryStats.uniqueRooms, icon: MapPin, color: 'text-violet-600', bg: 'bg-gradient-to-br from-violet-50 to-violet-100/60', iconBg: 'bg-violet-100' },
        { label: 'Hours/Week', value: summaryStats.totalHours, icon: Clock, color: 'text-amber-600', bg: 'bg-gradient-to-br from-amber-50 to-amber-100/60', iconBg: 'bg-amber-100' },
      ].map((stat, idx) => {
        const Icon = stat.icon;
        return (
          <div key={idx} className={`flex items-center gap-2.5 sm:gap-3 rounded-xl border border-slate-200/80 ${stat.bg} p-3 sm:p-3.5 shadow-sm hover:shadow-md transition-shadow`}>
            <div className={`rounded-lg p-2 sm:p-2.5 ${stat.iconBg} ${stat.color} shadow-sm`}>
              <Icon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
            </div>
            <div className="min-w-0">
              <div className="text-lg sm:text-xl font-bold leading-none tracking-tight">{stat.value}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate font-medium">{stat.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const Legend = () => {
    if (subjectColorMap.size === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 px-1 py-2 sm:py-2.5 bg-slate-50/60 rounded-xl border border-slate-100">
        <span className="text-[11px] sm:text-xs text-muted-foreground font-semibold ml-2 mr-1">Legend:</span>
        {Array.from(subjectColorMap.entries()).map(([code, colors]) => (
          <div key={code} className={`flex items-center gap-1.5 text-[11px] sm:text-xs bg-white border border-slate-200 rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-sm hover:shadow-md transition-shadow cursor-default`}>
            <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${colors.dot} flex-shrink-0 ring-2 ring-white`} />
            <span className="font-semibold text-slate-700">{code}</span>
          </div>
        ))}
      </div>
    );
  };

  const EmptyState = () => (
    <Card className="border-dashed border-2 border-slate-200 bg-gradient-to-b from-white to-slate-50/50">
      <CardContent className="text-center py-14 sm:py-16 px-6">
        <div className="mx-auto w-18 h-18 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-5 shadow-inner">
          <Calendar className="h-9 w-9 sm:h-10 sm:w-10 text-emerald-500" />
        </div>
        <h3 className="font-semibold text-lg sm:text-xl mb-2">No Classes Scheduled</h3>
        <p className="text-sm sm:text-base text-muted-foreground max-w-sm mx-auto leading-relaxed">
          You don't have any classes assigned for this semester. Contact your department for schedule updates.
        </p>
      </CardContent>
    </Card>
  );

  // ----- Mobile view -----

  if (isMobile) {
    const filteredDays = selectedDay !== null ? [selectedDay] : days.map((_, i) => i);
    const spotlightSchedule = ongoingClass || nextClass?.schedule || null;
    const spotlightDayIndex = ongoingClass ? nowDay : nextClass?.dayIndex;

    return (
      <div className="space-y-4">
        <StudentsListDialog />
        <SummaryBar />

        {spotlightSchedule && typeof spotlightDayIndex === 'number' && (
          <Card className={`overflow-hidden shadow-md transition-all ${ongoingClass ? 'border-emerald-300 ring-2 ring-emerald-200/50' : 'border-blue-200/70'}`}>
            <div className={`text-white px-4 py-3.5 ${ongoingClass ? 'bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {ongoingClass ? (
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                      </span>
                      <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-white/95">Live Now</p>
                    </div>
                  ) : (
                    <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-white/90">Up Next</p>
                  )}
                </div>
                <Badge className={`text-[11px] px-2 py-0.5 border ${ongoingClass ? 'bg-white/20 border-white/30 text-white' : 'bg-white/20 border-white/30 text-white'}`}>
                  {ongoingClass
                    ? 'In progress'
                    : (nextClass?.dayOffset || 0) === 0
                      ? 'Today'
                      : `In ${nextClass?.dayOffset} day${nextClass?.dayOffset === 1 ? '' : 's'}`}
                </Badge>
              </div>
              <h3 className="text-sm sm:text-base font-bold mt-1.5 leading-snug">
                {`${spotlightSchedule.subject?.code} • ${spotlightSchedule.subject?.name}`}
              </h3>
            </div>
            <CardContent className={`p-4 space-y-2.5 ${ongoingClass ? 'bg-emerald-50/50' : 'bg-blue-50/30'}`}>
              <div className="flex items-center text-xs sm:text-sm text-slate-700">
                <Calendar className={`w-4 h-4 mr-2 ${ongoingClass ? 'text-emerald-600' : 'text-blue-600'}`} />
                <span className="font-medium">
                  {days[spotlightDayIndex]}
                </span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-slate-700">
                <Clock className={`w-4 h-4 mr-2 ${ongoingClass ? 'text-emerald-600' : 'text-blue-600'}`} />
                <span className="font-medium">
                  {formatTimeRange(spotlightSchedule.startTime, spotlightSchedule.endTime)}
                </span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-slate-700">
                <MapPin className={`w-4 h-4 mr-2 ${ongoingClass ? 'text-emerald-600' : 'text-blue-600'}`} />
                <span className="font-medium">{spotlightSchedule.room?.name || 'TBA'}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Day filter chips */}
        <div className="relative">
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 scroll-smooth" role="tablist" aria-label="Filter by day" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <Button
              variant={selectedDay === null ? "default" : "outline"}
              size="sm"
              className={`rounded-full text-xs flex-shrink-0 h-10 px-5 font-semibold ${selectedDay === null ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/25' : 'border-slate-200'}`}
              onClick={() => setSelectedDay(null)}
            >
              All Days
            </Button>
            {days.map((day, index) => {
              const count = dayClassCounts[index];
              const hasClasses = count > 0;
              const isToday = index === today;
              return (
                <Button
                  key={day}
                  variant={selectedDay === index ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full text-xs flex-shrink-0 h-10 px-4 transition-all font-medium ${
                    selectedDay === index
                      ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/25'
                      : isToday
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-200'
                        : 'border-slate-200'
                  } ${!hasClasses ? 'opacity-40' : ''}`}
                  onClick={() => setSelectedDay(selectedDay === index ? null : index)}
                >
                  <span className="flex items-center gap-1.5">
                    {isToday && selectedDay !== index && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />}
                    {dayAbbrev[index]}
                  </span>
                  {hasClasses && (
                    <span className={`ml-1.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold leading-none ${selectedDay === index ? 'bg-white/25 text-white' : isToday ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {count}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Day cards */}
        {filteredDays.map((dayIndex) => {
          const day = days[dayIndex];
          const daySchedules = facultySchedules
            .filter(s => s.dayOfWeek === dayIndex)
            .sort((a: any, b: any) => {
              const [ah] = a.startTime.split(':').map(Number);
              const [bh] = b.startTime.split(':').map(Number);
              return ah - bh;
            });
          const isToday = dayIndex === today;

          if (selectedDay !== null && daySchedules.length === 0) return null;

          return (
            <Card key={day} className={`overflow-hidden transition-all duration-200 ${isToday ? 'ring-2 ring-emerald-400/50 shadow-md' : 'hover:shadow-md'}`}>
              <div className={`px-3 sm:px-4 py-3 sm:py-3.5 flex items-center gap-3 border-b ${isToday ? 'bg-gradient-to-r from-emerald-50 via-emerald-50/80 to-teal-50' : 'bg-slate-50/80'}`}>
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex flex-col items-center justify-center text-xs font-bold ${
                  isToday
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25'
                    : daySchedules.length > 0
                      ? 'bg-white border-2 border-slate-200 text-slate-700'
                      : 'bg-slate-100 border border-slate-200 text-slate-400'
                }`}>
                  <span className="text-xs sm:text-sm font-bold leading-none">{dayAbbrev[dayIndex]}</span>
                  {isToday && <span className="text-[8px] font-medium mt-0.5 opacity-80">Today</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold text-sm sm:text-base ${isToday ? 'text-emerald-700' : ''}`}>
                      {day}
                    </h3>
                    {isToday && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 py-0 px-1.5">Today</Badge>}
                  </div>
                  <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                    {daySchedules.length === 0 ? 'No classes — free day ✨' : `${daySchedules.length} class${daySchedules.length !== 1 ? 'es' : ''} scheduled`}
                  </p>
                </div>
                {daySchedules.length > 0 && (
                  <div className={`text-[10px] sm:text-[11px] font-medium px-2 py-0.5 rounded-full ${isToday ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {getDurationLabel(daySchedules.reduce((sum: number, s: any) => sum + getDurationMinutes(s), 0))}
                  </div>
                )}
              </div>

              <CardContent className="p-3 sm:p-3.5">
                {daySchedules.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
                      <Calendar className="w-5 h-5 text-slate-300" />
                    </div>
                    <p className="text-sm text-muted-foreground italic">No classes scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {daySchedules.map((schedule: any) => {
                      const colors = getColorForSchedule(schedule);
                      const durationMinutes = getDurationMinutes(schedule);
                      const isOngoing = dayIndex === nowDay && nowMinutes >= toMinutes(schedule.startTime) && nowMinutes < toMinutes(schedule.endTime);
                      const isExpanded = expandedMobileCard === schedule.id;
                      return (
                        <div
                          key={schedule.id}
                          className={`relative rounded-xl p-3.5 sm:p-4 ${colors.bg} border ${colors.border} transition-all duration-200 active:scale-[0.99] group ${
                            isOngoing ? 'ring-2 ring-emerald-400/60 shadow-md shadow-emerald-200/30' : 'hover:shadow-md'
                          }`}
                          onClick={() => setExpandedMobileCard(isExpanded ? null : schedule.id)}
                          role="button"
                          tabIndex={0}
                        >
                          {isOngoing && (
                            <div className="absolute -top-px -right-px">
                              <div className="flex items-center gap-1 bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-bl-lg rounded-tr-xl">
                                <span className="relative flex h-1.5 w-1.5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                                </span>
                                Live
                              </div>
                            </div>
                          )}
                          <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${colors.accent}`} />
                          <div className="pl-3 sm:pl-3.5 space-y-2">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <Badge variant="outline" className={`text-[11px] sm:text-xs font-semibold ${colors.badge}`}>
                                {schedule.subject?.code}
                              </Badge>
                              <Badge variant="outline" className="h-5 text-[10px] sm:text-[11px] px-2 bg-white/80 border-slate-200 text-slate-600">
                                {getDurationLabel(durationMinutes)}
                              </Badge>
                            </div>
                            <div className={`font-semibold text-sm sm:text-[15px] leading-snug ${colors.text}`}>
                              {schedule.subject?.name}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                              <div className="flex items-center text-[11px] sm:text-xs text-slate-700 bg-white/70 rounded-lg px-2.5 py-1.5">
                                <Clock className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                <span className="font-medium">{formatTimeRange(schedule.startTime, schedule.endTime)}</span>
                              </div>
                              <div className="flex items-center text-[11px] sm:text-xs text-slate-600">
                                <MapPin className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                <span className="font-medium">{schedule.room?.name || 'TBA'}</span>
                              </div>
                            </div>
                            <div className="pt-0.5">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] px-2.5 bg-white/90 border-slate-200"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  loadClassStudents(schedule);
                                }}
                              >
                                <Users className="h-3.5 w-3.5 mr-1" />
                                Students List
                              </Button>
                            </div>
                            {isExpanded && (
                              <div className="pt-1.5 mt-1 border-t border-slate-200/60 space-y-1.5 animate-in slide-in-from-top-1 duration-200">
                                <div className="flex items-center text-[11px] sm:text-xs text-slate-600">
                                  <BookOpen className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                  <span>{schedule.subject?.name || 'N/A'}</span>
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  Tap to collapse
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {facultySchedules.length === 0 && <EmptyState />}
        <Legend />
      </div>
    );
  }

  // ----- Desktop: precompute grid placements -----

  const occupiedCells = new Set<string>();
  const schedulePlacements: { schedule: any; dayIndex: number; rowStart: number; rowSpan: number }[] = [];

  timeSlots.forEach((timeSlot, rowIndex) => {
    days.forEach((_day, dayIndex) => {
      const schedule = getScheduleForTimeSlot(dayIndex, timeSlot);
      if (schedule) {
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const slotHour = parseInt(timeSlot.split(':')[0]);
        if (startHour === slotHour) {
          const duration = getScheduleDuration(schedule);
          schedulePlacements.push({ schedule, dayIndex, rowStart: rowIndex, rowSpan: duration });
          for (let i = 0; i < duration; i++) {
            occupiedCells.add(`${dayIndex}-${rowIndex + i}`);
          }
        }
      }
    });
  });

  // Current-time indicator
  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  const currentTimeSlotIndex = timeSlots.findIndex(slot => parseInt(slot.split(':')[0]) === currentHour);

  // ----- Desktop grid view -----

  return (
    <TooltipProvider>
      <div className="w-full space-y-4">
        <StudentsListDialog />
        <SummaryBar />

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
          <div
            className="grid min-w-[700px]"
            style={{
              gridTemplateColumns: '60px repeat(7, minmax(80px, 1fr))',
              gridTemplateRows: `48px repeat(${timeSlots.length}, minmax(64px, auto))`,
            }}
          >
            {/* Corner cell */}
            <div className="bg-slate-50 border-b border-r border-slate-200 flex items-center justify-center" style={{ gridRow: 1, gridColumn: 1 }}>
              <Clock className="h-4 w-4 text-slate-400" />
            </div>

            {/* Day header row */}
            {days.map((day, i) => {
              const isToday = i === today;
              const hasClasses = facultySchedules.some(s => s.dayOfWeek === i);
              const dayClassCount = facultySchedules.filter(s => s.dayOfWeek === i).length;
              return (
                <div
                  key={day}
                  className={`flex flex-col items-center justify-center py-2 border-b border-slate-200 transition-colors ${
                    i < days.length - 1 ? 'border-r' : ''
                  } ${isToday ? 'bg-emerald-50' : 'bg-slate-50'}`}
                  style={{ gridRow: 1, gridColumn: i + 2 }}
                >
                  <span className={`text-[13px] font-semibold ${isToday ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {day.slice(0, 3)}
                  </span>
                  {isToday && (
                    <span className="mt-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-100 rounded-full px-1.5 py-px leading-tight">Today</span>
                  )}
                  {!isToday && hasClasses && (
                    <span className="mt-0.5 text-[10px] text-slate-400 font-medium">{dayClassCount} cls</span>
                  )}
                </div>
              );
            })}

            {/* Time labels */}
            {timeSlots.map((timeSlot, rowIndex) => {
              const isCurrentSlot = currentTimeSlotIndex === rowIndex;
              return (
                <div
                  key={`time-${timeSlot}`}
                  className={`text-[11px] font-medium text-right pr-2 pt-2 border-r border-slate-200 relative ${
                    rowIndex % 2 === 0 ? '' : 'bg-slate-50/40'
                  } ${
                    isCurrentSlot ? 'text-red-600 font-semibold' : 'text-slate-500'
                  }`}
                  style={{ gridRow: rowIndex + 2, gridColumn: 1 }}
                >
                  {formatTo12Hour(timeSlot)}
                  {isCurrentSlot && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500 translate-x-1 z-10 shadow-sm" />
                  )}
                </div>
              );
            })}

            {/* Current-time indicator line */}
            {currentTimeSlotIndex >= 0 && currentTimeSlotIndex < timeSlots.length && (
              <div
                className="pointer-events-none z-20"
                style={{
                  gridRow: currentTimeSlotIndex + 2,
                  gridColumn: '2 / -1',
                  position: 'relative',
                }}
              >
                <div
                  className="absolute left-0 right-0 border-t-2 border-red-500/80"
                  style={{ top: `${(currentMinute / 60) * 100}%` }}
                >
                  <div className="absolute -left-[3px] -top-[5px] w-[10px] h-[10px] rounded-full bg-red-500 shadow-sm" />
                </div>
              </div>
            )}

            {/* Schedule blocks */}
            {schedulePlacements.map((entry) => {
              const colors = getColorForSchedule(entry.schedule);
              const isHovered = hoveredSchedule === entry.schedule.id;
              const isBlockOngoing = entry.schedule.id === ongoingClass?.id;
              const isBlockNext = !isBlockOngoing && entry.schedule.id === nextClass?.schedule?.id;
              return (
                <Tooltip key={`schedule-${entry.schedule.id}-${entry.dayIndex}`}>
                  <TooltipTrigger asChild>
                    <div
                      className={`relative rounded-lg p-2 lg:p-2.5 mx-0.5 my-0.5 border-l-[3px] overflow-hidden cursor-pointer
                        transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover}
                        ${isHovered ? 'shadow-lg scale-[1.02] z-10' : 'shadow-sm hover:shadow-md hover:z-10'}
                        ${isBlockOngoing ? 'ring-2 ring-emerald-400 ring-offset-1 shadow-lg shadow-emerald-200/40 z-10' : ''}
                        ${isBlockNext ? 'ring-1 ring-blue-300/80 ring-offset-1 shadow-md shadow-blue-100/40' : ''}`}
                      style={{
                        gridRow: `${entry.rowStart + 2} / span ${entry.rowSpan}`,
                        gridColumn: entry.dayIndex + 2,
                      }}
                      onMouseEnter={() => setHoveredSchedule(entry.schedule.id)}
                      onMouseLeave={() => setHoveredSchedule(null)}
                    >
                      {isBlockOngoing && (
                        <div className="absolute top-1 right-1 z-10">
                          <Badge className="text-[9px] h-[18px] px-1.5 bg-emerald-600 hover:bg-emerald-600 text-white shadow-sm flex items-center gap-1">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                            </span>
                            LIVE
                          </Badge>
                        </div>
                      )}
                      {isBlockNext && (
                        <div className="absolute top-1 right-1 z-10">
                          <Badge variant="outline" className="text-[9px] h-[18px] px-1.5 border-blue-300 text-blue-600 bg-blue-50/90 shadow-sm">NEXT</Badge>
                        </div>
                      )}
                      <div className="flex flex-col h-full justify-between gap-1">
                        <div>
                          <Badge variant="outline" className={`text-[10px] lg:text-[11px] font-semibold mb-1 ${colors.badge}`}>
                            {entry.schedule.subject?.code}
                          </Badge>
                          <div className={`font-semibold text-xs lg:text-[13px] leading-snug ${colors.text}`}>
                            {entry.schedule.subject?.name}
                          </div>
                        </div>
                        <div className="space-y-0.5 mt-auto">
                          <div className="flex items-center text-[10px] lg:text-[11px] text-slate-600">
                            <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{entry.schedule.room?.name || 'TBA'}</span>
                          </div>
                          <div className="flex items-center text-[10px] lg:text-[11px] text-slate-600">
                            <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span>{formatTimeRange(entry.schedule.startTime, entry.schedule.endTime)}</span>
                          </div>
                          <div className="pt-0.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px] px-2 bg-white/90 border-slate-200"
                              onClick={(event) => {
                                event.stopPropagation();
                                loadClassStudents(entry.schedule);
                              }}
                            >
                              <Users className="h-3 w-3 mr-1" />
                              List
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-1.5">
                      <div className="font-bold text-sm">{entry.schedule.subject?.code} - {entry.schedule.subject?.name}</div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeRange(entry.schedule.startTime, entry.schedule.endTime)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <MapPin className="w-3.5 h-3.5" />
                        {entry.schedule.room?.name || 'TBA'}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Calendar className="w-3.5 h-3.5" />
                        {getDayName(entry.dayIndex)}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}

            {/* Empty cells */}
            {timeSlots.map((_timeSlot, rowIndex) =>
              days.map((_day, dayIndex) => {
                if (occupiedCells.has(`${dayIndex}-${rowIndex}`)) return null;
                const isToday = dayIndex === today;
                const isAlternateRow = rowIndex % 2 !== 0;
                return (
                  <div
                    key={`empty-${dayIndex}-${rowIndex}`}
                    className={`border-b border-r border-slate-200/60 transition-colors ${
                      isToday
                        ? isAlternateRow ? 'bg-emerald-50/30' : 'bg-emerald-50/15'
                        : isAlternateRow ? 'bg-slate-50/50' : ''
                    } hover:bg-slate-100/40`}
                    style={{ gridRow: rowIndex + 2, gridColumn: dayIndex + 2 }}
                  />
                );
              })
            )}
          </div>
        </div>

        <Legend />
        {facultySchedules.length === 0 && <EmptyState />}
      </div>
    </TooltipProvider>
  );
}